<template>
<div>
  <el-row type="flex" class="title-bar" :style="'justify-content:' + (operationFlag ? 'flex-end;' : 'space-between;')">
    <w-perf-analysis-time-info v-if="!operationFlag" :ref="timeInfoRef" :checkDatetime="checkDatetime" @change="onTimeTypeChange" @reload="onCurrentDataReLoad" />
    <span>
      <el-radio-group class="mr-r10" v-model="searchLogType" @change="loadLogData">
        <el-radio label="origin">원본 로그</el-radio>
        <el-radio label="filtering">필터링 로그</el-radio>
      </el-radio-group>
      <el-select v-model="searchTypeCheck" v-show="searchLogType === 'filtering'" :disabled="searchLogType === 'origin'" @change="loadLogData">
        <el-option label="전체" value="total"></el-option>
        <el-option label="확인" value="checked"></el-option>
        <el-option label="미확인" value="unchecked"></el-option>
      </el-select>
      <el-button size="small" v-show="searchLogType === 'filtering'" @click="changeIsChecked" :disabled="disabledCheck">확인</el-button>
      <el-button size="small" v-show="searchLogType === 'filtering'" @click="changeAllIsChecked" :disabled="total === 0">전체확인</el-button>
      <w-header-search-button @click="onShowSearchButtonClick"></w-header-search-button>
    </span>
  </el-row>
  <el-row>
    <el-table-wrapper v-loading="loading" :data="tableData" :total="total" :columns="columns" :column-default="columnDefault" :show-custom-header="true" :show-search-tool="showSearchTool"
    @selection-change="onSelectionChange" @pagination-change="onCurrentPageChange" ref="logTable" style="width:100%;" :show-overflow-tooltip="true" :pagination="true">
    <template slot-scope="scope" slot="is-checked-slot">
      <span v-if="scope.row.isChecked === '1'">확인</span>
      <span v-else-if="scope.row.isChecked === '0'">미확인</span>
      <span v-else>-</span>
    </template>
    </el-table-wrapper>
  </el-row>
</div>
</template>

<script>
import { getDateToString, getIntervalDatetimeParams } from '@/utils/date';
import { isNil, forEach, findIndex, isEmpty, cloneDeep, concat } from 'lodash';

import { EMPTY_TEXT } from '@/utils/string';
import { analysisTimeType } from '@/constants/performance';
import { columnDefault } from '@/constants/table';
import { mapGetters } from 'vuex';
import msgUtil from '@/utils/message';
import analysisTableMixin from '@/mixins/performance/analysis-table-ex';
import APPLogAPI from '@/api/log/common';
import WPerfAnalysisTimeInfo from '@/views/performance/node/analysis/components/PerfAnalysisTimeInfo';

export default {
  name: 'WAppLogTable',
  mixins: [analysisTableMixin],
  components: {
    WPerfAnalysisTimeInfo,
  },
  data() {
    return {
      columnDefault,
      filteringLogColumns: [
        {
          type: 'selection',
          width: '50',
        },
        {
          prop: 'isChecked',
          label: '상태',
          width: 60,
          scopedSlot: 'is-checked-slot',
        },
      ],
      searchLogType: 'origin',
      searchTypeCheck: 'total',
      convertDateType: null,
      checkDatetime: null,
      baseDatetime: null,
      loading: false,
      total: 0,
      limit: 50,
      start: 0,
      tableData: [],
      checkedRow: [],
      showSearchTool: false,
    };
  },
  computed: {
    ...mapGetters(['activeManagetypeIds']),
    isLms() {
      return findIndex(this.activeManagetypeIds, (item) => item === 'LMS') >= 0;
    },
    columns() {
      const idx = findIndex(this.activeManagetypeIds, (item) => item === 'LMS');
      let logColumns = cloneDeep(this.filteringLogColumns);
      if (idx === -1) {
        logColumns = concat(logColumns, [
          {
            prop: 'filterName',
            label: '필터',
            width: 150,
            showOverflowTooltip: true,
            searchable: true,
            sortable: true,
            formatter: this.getStringFormatter,
          },
          {
            prop: 'message',
            label: '메시지',
            searchable: true,
            sortable: true,
            formatter: this.getStringFormatter,
          },
          {
            prop: '@timestamp',
            label: '수집시각',
            showOverflowTooltip: true,
            formatter: this.getDateFormatter,
            width: 200,
            sortable: true,
          },
        ]);
      } else {
        logColumns = concat(logColumns, [
          {
            prop: 'sourceName',
            label: '로그 소스',
            width: 150,
            searchable: true,
            sortable: true,
            formatter: this.getStringFormatter,
          },
          {
            prop: 'originMessage',
            label: '원본 메시지',
            searchable: true,
            sortable: true,
            formatter: this.getStringFormatter,
          },
          {
            prop: 'filterName',
            label: '정책 이름',
            width: 150,
            searchable: true,
            sortable: true,
            formatter: this.getStringFormatter,
          },
          {
            prop: 'timestamp',
            label: '수집 시간',
            width: 160,
            searchable: true,
            sortable: true,
            searchType: 'date',
            formatter: this.getDateFormatter,
          },
        ]);
      }

      return logColumns;
    },
    disabledCheck() {
      return !(this.searchLogType === 'filtering' && !isEmpty(this.checkedRow));
    },
  },
  methods: {
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        if (this.isLms) {
          return getDateToString(getIntervalDatetimeParams(value, 'hour', 9), 'YYYY-MM-DD HH:mm:ss');
        }
        return getDateToString(value, 'YYYY-MM-DD HH:mm:ss');
      }
      return EMPTY_TEXT;
    },
    /**
     * 데이터를 조회한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadData() {
      if (this.$refs.logTable) {
        this.loadLogData();
      } else {
        this.$nextTick(() => {
          this.loadLogData();
        });
      }
    },
    /**
     * 어플리케이션 로그 데이터를 조회한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadLogData() {
      const params = this.setLogSearchParams();

      this.loading = true;
      APPLogAPI.getLogAnalysisData(params).then((response) => {
        if (response.data.success) {
          this.tableData = response.data.data;
          this.total = response.data.total;
          this.checkDatetime = this.baseDatetime;
        } else {
          this.failToLoad();
        }
      }).catch((e) => {
        this.failToLoad();
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * 어플리케이션 로그 조회를 위한 파라미터를 설정한다.
     * @author Ju
     */
    setLogSearchParams() {
      this.baseDatetime = this.timeType === analysisTimeType.CURRENT.value ? new Date().getTime() : this.plotLineDatetime;
      const dateParams = this.getDatetimeParams(this.baseDatetime, true);
      const tableSearchParams = this.$refs.logTable.getParameter();

      // 기본 파라미터
      const params = {
        objId: this.selectedNode.obj.objId,
        logType: 'APP',
        type: 'performance',
        startDateTime: dateParams.startDatetime,
        endDateTime: dateParams.endDatetime,
        start: tableSearchParams.start,
        limit: tableSearchParams.limit,
        queryList: [],
      };

      // 헤더 검색어 추가
      if (!isNil(tableSearchParams.searchParams)) {
        const queryList = [];
        forEach(tableSearchParams.searchParams, (item) => {
          queryList.push({ field: item.field, value: item.value, operator: 'AND' });
        });
        params.queryList = queryList;
      }

      // 1. 원본/필터링 로그 조회 타입 추가 (라디오)
      if (this.searchLogType === 'filtering') {
        // 필터링 로그
        params.queryList.push({ field: 'filterId', operator: 'EXIST' });

        // 2. 확인/미확인 조회 타입 추가 (콤보박스)
        if (this.searchTypeCheck === 'checked') {
          // 확인만
          params.queryList.push({ field: 'isChecked', value: '1', operator: 'AND' });
        } else if (this.searchTypeCheck === 'unchecked') {
          // 미확인만
          params.queryList.push({ field: 'isChecked', value: '0', operator: 'AND' });
        }
      }

      return params;
    },
    /**
     * 데이터 로드 실패 처리
     * @author Ju
     * @return {[type]} [description]
     */
    failToLoad() {
      this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '어플리케이션 로그', false));
    },
    onShowSearchButtonClick() {
      this.showSearchTool = !this.showSearchTool;
    },
    getStringFormatter(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    /**
     * 로그 상태 변경 (확인/ 미확인)
     * @author leesy918
     * @return {[type]}       [description]
     */
    changeIsChecked() {
      const checkedRow = [];
      this.$confirm(...msgUtil.getBasicConfirmData(msgUtil.action.CHECK)).then(() => {
        this.checkedRow.forEach((item) => {
          if (item.isChecked === '0') {
            checkedRow.push(item);
          }
        });
        if (isEmpty(checkedRow)) {
          this.$message(msgUtil.getCustomMsgData('변경사항이 없습니다.'));
          this.$refs.logTable.$children[0].clearSelection();
          return;
        }
        const params = checkedRow;
        this.loading = true;
        APPLogAPI.updateLogIsChecked(params).then((response) => {
          if (response.data.success) {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', response.data.success));
            this.loadData();
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', response.data.success));
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', false));
          throw new Error(e);
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },
    /**
     * 현재 검색 조건의 모든 로그들의 상태를 확인으로 변경한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    changeAllIsChecked() {
      this.$confirm(...msgUtil.getCustomConfirmData(`${this.total}건의 로그를 모두 확인 처리하시겠습니까?`)).then(() => {
        const params = this.setLogSearchParams();
        params.total = this.total;
        params.queryList.push({ field: 'isChecked', value: '0' });
        this.loading = true;
        APPLogAPI.updateAllLogIsChecked(params).then((response) => {
          if (response.data.success) {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', true));
            this.loadData();
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', false));
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.CHECK, '로그', false));
          throw new Error(e);
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },
    /**
     * 확인할 로그 체크박스 판단 여부
     * @author leesy918
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     */
    onSelectionChange(data) {
      this.checkedRow = data;
    },
    onCurrentPageChange(page) {
      this.limit = page.pageSize;
      this.start = (page.currentPage - 1) * this.limit;
      this.loadLogData();
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/database/components/table/Applog/index.vue